// src/utils/apiHelpers.js

/**
 * Generic helper to call any of the five API functions:
 *  - getAll
 *  - getById
 *  - createOne
 *  - updateOne
 *  - deleteOne
 *
 * Usage:
 *   // fetch all users
 *   fetchData(getAll, { apiName: 'users' }, setUsers);
 *
 *   // fetch one user
 *   fetchData(getById, { apiName: 'users', id: 42 }, setUser);
 *
 *   // create
 *   fetchData(create, { apiName: 'users', data: { name: 'Alice' } }, setNewUser);
 *
 *   // update
 *   fetchData(update, { apiName: 'users', id: 42, data: { name: 'Bob' } }, null);
 *
 *   // delete
 *   fetchData(deleteOne, { apiName: 'users', id: 42 }, null);
 */
const CACHEABLE_TABLES = [
  'asset-criticality',
  //'asset-type',
  //'category',
  //'cause-codes',
  // 'class',
  'colours',
  //'cost-centres',
  'currency',
  'event-delay-codes',
  'event-status',
  'event-type',
  'event-location',
  //'expense-codes',
  //'failure-codes',
  //'form-input-type',
  'form-type',
  //'hierarchy-structures',
  //'manufacturers',
  'measurement-units',
  'meters',
  'notification-status',
  'organisation',
  'parts-category',
  'planning-group',
  'planning-horizon',
  'planning-types',
  'plant-status',
  'priority',
  'project-status',
  //'roles',
  'serial-status',
  'purchase-order-status',
  //'settings',
  'task-types',
  //'task-codes',
  //'modifier-codes',
  //'trades',
  //'user-profile',
  'work-order-status'
];

export const fetchData = async (
  apiFunc,
  { apiName, id = null, data = null },
  setState = null
) => {
  try {
    const isCacheable = CACHEABLE_TABLES.includes(apiName);
    const opName = apiFunc.name;
    const cacheKey = `cache_${apiName}`;

    if (isCacheable && opName === 'getAll') {
      const cached = sessionStorage.getItem(cacheKey);
      if (cached) {
        const parsed = JSON.parse(cached);
        if (setState) setState(parsed);
        return parsed;
      }
    }

    let response;

    if (data !== null) {
      // create or update
      response = id !== null
        ? await apiFunc(apiName, id, data)  // update
        : await apiFunc(apiName, data);     // create
    } else {
      // getAll, getById, or delete
      response = id !== null
        ? await apiFunc(apiName, id)        // getById or deleteOne
        : await apiFunc(apiName);           // getAll
    }

    if (isCacheable) {
      if (opName === 'getAll') {
        sessionStorage.setItem(cacheKey, JSON.stringify(response));
      } else if (['create', 'update', 'deleteOne'].includes(opName)) {
        sessionStorage.removeItem(cacheKey);
      }
    }

    if (setState) {
      setState(response);
    }

    return response;
  } catch (err) {
    console.error(`Error in ${apiFunc.name} ${apiName}${id?'/'+id:''}:`, err);
    throw err;
  }
};
